// WorkforceManagement.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink // Import Link from Material-UI for clickable breadcrumbs
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import WorkforceManagementConfig from "../../components/WorkforceManagementConfig";
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom"; // Import useOutletContext and useNavigate

// Definisikan data tab di luar komponen untuk menghindari re-render yang tidak perlu.
const tabConfigurations = [
    {
        label: "User",
        dbPath: "db_user/tb_user", // Path yang diinginkan
        fieldName: "nama_user",
        primaryKeyField: "uid", // Menggunakan 'uid' sebagai primary key
    },
    {
        label: "Departemen",
        dbPath: "db_config/db_wfms/tb_department",
        fieldName: "nama_department",
        primaryKeyField: "id_department",
    },
    {
        label: "Jabatan",
        dbPath: "db_config/db_wfms/tb_jabatan",
        fieldName: "nama_jabatan",
        primaryKeyField: "id_jabatan",
    },
    {
        label: "Status Tenaga Kerja",
        dbPath: "db_config/db_wfms/tb_status_tenaga_kerja",
        fieldName: "nama_status_tenaga_kerja",
        primaryKeyField: "id_status_tenaga_kerja",
    },
    {
        label: "Status Pekerjaan",
        dbPath: "db_config/db_wfms/tb_status_pekerjaan",
        fieldName: "nama_status_pekerjaan",
        primaryKeyField: "id_status_pekerjaan",
    },
    {
        label: "Index",
        dbPath: "db_config/db_wfms/tb_index",
        fieldName: "nomor_index",
        primaryKeyField: "id_index",
    },
    {
        label: "Role",
        dbPath: "db_config/db_wfms/tb_role",
        fieldName: "nama_role",
        primaryKeyField: "id_role",
    },
    {
        label: "Email",
        dbPath: "db_config/db_wfms/tb_email",
        fieldName: "alamat_email",
        primaryKeyField: "id_email",
    },
];

function WorkforceManagement() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate(); // For navigating with breadcrumbs

    const { breadcrumbs } = useOutletContext();


    // State untuk tabIndex, mengambil dari localStorage atau default ke 'User' (index 0).
    const [tabIndex, setTabIndex] = useState(() => {
        const storedTabIndex = localStorage.getItem('workforceManagementTabIndex');
        // Pastikan nilai yang disimpan valid (angka dan dalam rentang tabConfigurations)
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        // Jika tidak ada di localStorage atau tidak valid, default ke index 'User'
        const defaultUserIndex = tabConfigurations.findIndex(config => config.label === "User");
        return defaultUserIndex !== -1 ? defaultUserIndex : 0;
    });

    // Simpan tabIndex ke localStorage setiap kali berubah
    useEffect(() => {
        localStorage.setItem('workforceManagementTabIndex', tabIndex.toString());
    }, [tabIndex]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Dapatkan konfigurasi tab yang aktif.
    const currentTabConfig = tabConfigurations[tabIndex];

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    // Render Link if path exists, otherwise render Typography
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Konten utama */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render WorkforceManagementConfig secara dinamis berdasarkan tabIndex */}
                            {currentTabConfig && (
                                <WorkforceManagementConfig
                                    title={`Daftar ${currentTabConfig.label}`} // Sesuaikan judul
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Sidebar desktop */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start",
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {/* Loop melalui konfigurasi tab untuk sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {/* Loop melalui konfigurasi tab untuk mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default WorkforceManagement;